import React, { useState, useEffect } from "react";
import { X, AlertTriangle, Send, Loader } from "lucide-react";
import { useSelector } from "react-redux";
import { apiClient } from "../../config/api";
import API_CONFIG from "../../config/api";

const SEVERITY_OPTIONS = ["Critical", "High", "Medium", "Low"];
const STATUS_OPTIONS = ["New", "Open", "In Progress"];

export default function CreateIncidentModal({ onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    severity: "Medium",
    status: "New",
    clientUUID: "",
    sourceSystem: "Manual",
    providerName: "Vorxoc Portal",
    classification: "",
    type: "SecurityIncident",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Get clients from Redux store
  const clients = useSelector((state) => state.onboarding.clients) || [];
  const selectedClientId = useSelector((state) => state.onboarding.selectedClientId);

  // Set default client when modal opens
  useEffect(() => {
    if (selectedClientId) {
      setFormData((prev) => ({ ...prev, clientUUID: selectedClientId }));
    } else if (clients.length > 0) {
      setFormData((prev) => ({ ...prev, clientUUID: clients[0].clientUUID }));
    }
  }, [clients, selectedClientId]);

  // Add blur effect to header and sidebar when modal opens
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate required fields
    if (!formData.title.trim()) {
      setError("Title is required");
      return;
    }
    if (!formData.severity) {
      setError("Severity is required");
      return;
    }
    if (!formData.clientUUID) {
      setError("Client is required");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await apiClient.post(API_CONFIG.ENDPOINTS.INCIDENTS, {
        title: formData.title.trim(),
        description: formData.description.trim(),
        severity: formData.severity,
        status: formData.status,
        clientUUID: formData.clientUUID,
        sourceSystem: formData.sourceSystem,
        providerName: formData.providerName,
        classification: formData.classification || undefined,
        type: formData.type,
      });

      if (response.data?.success) {
        onSuccess?.(response.data.data);
        onClose();
      } else {
        setError(response.data?.error || "Failed to create incident");
      }
    } catch (err) {
      console.error("Create incident error:", err);
      if (err.response?.status === 403) {
        setError("Only Helxon administrators can create incidents");
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(err.message || "Failed to create incident");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSeverityClass = (severity) => {
    switch (severity) {
      case "Critical":
        return "badge badge--critical";
      case "High":
        return "badge badge--high";
      case "Medium":
        return "badge badge--medium";
      case "Low":
        return "badge badge--low";
      default:
        return "badge";
    }
  };

  return (
    <div className="incident-detail-overlay">
      <div className="incident-detail-modal create-incident-modal">
        {/* Header */}
        <div className="incident-detail__header">
          <div className="incident-detail__title-section">
            <div className="incident-detail__id-section">
              <AlertTriangle size={20} className="create-incident__icon" />
              <h2 className="incident-detail__title">Create New Incident</h2>
            </div>
            <p className="incident-detail__description">
              Create a new security incident for a client
            </p>
          </div>
          <button className="incident-detail__close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Form Content */}
        <div className="incident-detail__content-wrapper">
          <form className="create-incident__form" onSubmit={handleSubmit}>
            {/* Error Message */}
            {error && (
              <div className="create-incident__error">
                <AlertTriangle size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Title */}
            <div className="create-incident__field">
              <label className="create-incident__label" htmlFor="title">
                Title <span className="required">*</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                className="create-incident__input"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter incident title"
                maxLength={500}
                required
              />
            </div>

            {/* Description */}
            <div className="create-incident__field">
              <label className="create-incident__label" htmlFor="description">
                Description
              </label>
              <textarea
                id="description"
                name="description"
                className="create-incident__textarea"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter incident description"
                rows={4}
              />
            </div>

            {/* Two Column Layout */}
            <div className="create-incident__row">
              {/* Client */}
              <div className="create-incident__field">
                <label className="create-incident__label" htmlFor="clientUUID">
                  Client <span className="required">*</span>
                </label>
                <select
                  id="clientUUID"
                  name="clientUUID"
                  className="create-incident__select"
                  value={formData.clientUUID}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a client</option>
                  {clients.map((client) => (
                    <option key={client.clientUUID} value={client.clientUUID}>
                      {client.clientName || client.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Severity */}
              <div className="create-incident__field">
                <label className="create-incident__label" htmlFor="severity">
                  Severity <span className="required">*</span>
                </label>
                <div className="create-incident__severity-options">
                  {SEVERITY_OPTIONS.map((sev) => (
                    <button
                      key={sev}
                      type="button"
                      className={`create-incident__severity-btn ${
                        formData.severity === sev ? "is-selected" : ""
                      } ${getSeverityClass(sev)}`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, severity: sev }))
                      }
                    >
                      {sev}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="create-incident__row">
              {/* Status */}
              <div className="create-incident__field">
                <label className="create-incident__label" htmlFor="status">
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  className="create-incident__select"
                  value={formData.status}
                  onChange={handleChange}
                >
                  {STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>

              {/* Classification */}
              <div className="create-incident__field">
                <label className="create-incident__label" htmlFor="classification">
                  Classification
                </label>
                <input
                  type="text"
                  id="classification"
                  name="classification"
                  className="create-incident__input"
                  value={formData.classification}
                  onChange={handleChange}
                  placeholder="e.g., Undetermined, TruePositive"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="create-incident__actions">
              <button
                type="button"
                className="btn btn--outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader size={16} className="spinning" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    Create Incident
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

